import {Centrifuge} from 'centrifuge';
import type {Subscription} from 'centrifuge';
import {IDLE_CLOSE_DELAY_MS, ImmediateCheckError, SubscribeRejectedError} from './stream-client.library';
import type {JobReady} from './stream-client.library';

interface TokenResult {
  token: string;
  ns: string;
}

interface CentrifugoDeps {
  getToken: () => Promise<TokenResult>;
  /** Base URL of the Centrifugo endpoint. Defaults to window.PMW.streamClientUrl. */
  clientBaseUrl?: string;
  /** Test seam: build the underlying Centrifuge instance. */
  centrifugeFactory?: (websocketUrl: string, options: {getToken: () => Promise<string>}) => Centrifuge;
}

interface ResolverEntry {
  /** Every concurrent observer of this key. Keys are the backend-assigned
   *  pollingKey (one per job), so distinct jobs never collide here - even two
   *  uploads of the same image get different keys. Sharing happens only when
   *  the SAME job is awaited twice at once: a resume re-attaching to a job its
   *  kickoff is still awaiting, or a StrictMode double-invoke. One subscription
   *  and one probe serve them all; a result fans out to every waiter. Note that
   *  cancelJob is keyed too, so a bail tears the entry down for every waiter -
   *  a stranded co-observer falls back via its own fast-path timeout. */
  resolvers: ((r: JobReady) => void)[];
  rejecters: ((e: unknown) => void)[];
  probeTimer: ReturnType<typeof setInterval> | null;
  subscription: Subscription | null;
}

/** Matches the SSE client's probe cadence; see stream-client.library.ts. */
const PROBE_INTERVAL_MS = 30_000;

/**
 * centrifuge-js 'unsubscribed' code ranges: codes below 100 are
 * client-initiated (our own cleanup calling unsubscribe()); 100 and above are
 * server-sent rejections (102 unknown channel, 103 permission denied, 107 bad
 * request, ...). Only the server range means "no publication can ever arrive".
 */
const SERVER_UNSUBSCRIBE_CODE_MIN = 100;

/** Poll-key grammar, mirroring PHP CentrifugoChannel::KEY_PATTERN. */
const CHANNEL_SAFE_KEY_PATTERN = /^[A-Za-z0-9._-]{8,128}$/;

/**
 * Builds the user-limited Centrifugo channel for a job. MUST stay byte-identical
 * to PHP's CentrifugoChannel::forJob(); the shared fixture
 * (ci4-stock/tests/fixtures/stream-channel-contract.json) pins both sides, and
 * both sides reject keys outside the poll-key grammar (a '#' would relocate the
 * user-limit suffix) so a violation fails loudly instead of building an unsafe
 * channel. The '#<ns>' suffix is the user-limited security boundary - see the
 * PHP class docblock for the full rationale.
 */
export function centrifugoChannelForJob(ns: string, key: string): string {
  if (!CHANNEL_SAFE_KEY_PATTERN.test(key)) {
    throw new Error(`centrifugo-stream-client: job key is not channel-safe: ${key}`);
  }
  return `jobs:${key}#${ns}`;
}

/** A promise that never resolves - used so only a real JobReady result wins the race. */
const PENDING_FOREVER = new Promise<JobReady>(() => {
  // Intentionally left pending - no-op race arm.
});

/**
 * Centrifugo-backed realtime client for job-ready notifications: the successor
 * transport to {@link StreamClient} (see the Centrifugo migration
 * recommendation). Implements the same seam consumed by
 * tryStreamPollingFastPath - warm / isConnected / awaitJob / cancelJob - so the
 * fast-path wrapper, legacy callers, and telemetry are transport-blind.
 *
 * Deliberately NOT a port of StreamClient: centrifuge-js owns connection
 * lifecycle, reconnect with jittered backoff, token refresh (getToken), and
 * subscription recovery, which is most of what that class hand-rolled around
 * EventSource. What remains here is the job-resolver bookkeeping: one
 * user-limited channel per job (`jobs:<key>#<ns>`), subscribed with `since` so
 * cache recovery delivers a publication that happened before the subscribe
 * (the publish-before-subscribe race), unsubscribed as soon as the job
 * resolves, and the same immediate-check + 30s store probe safety net -
 * the poll store remains the source of truth and every failure degrades to
 * long polling.
 */
export class CentrifugoStreamClient {
  #centrifuge: Centrifuge | null = null;
  #ns: string | null = null;
  #connecting: Promise<void> | null = null;
  #connected = false;
  #idleTimer: ReturnType<typeof setTimeout> | null = null;
  readonly #resolvers = new Map<string, ResolverEntry>();
  readonly #clientBaseUrl: string;

  constructor(private readonly deps: CentrifugoDeps) {
    this.#clientBaseUrl = deps.clientBaseUrl ?? window.PMW.streamClientUrl;
  }

  /** True while the underlying Centrifugo connection is established. */
  isConnected(): boolean {
    return this.#connected;
  }

  /**
   * Eagerly connects so an imminent job reuses the socket instead of paying the
   * cold handshake inside the fast path's 5s connect budget. Best-effort,
   * idempotent, never throws.
   */
  warm(): void {
    void this.#ensureConnected()
      .then(() => {
        this.#armIdleCloseIfIdle();
      })
      .catch(() => {
        // swallow: warming is best-effort; the first awaitJob retries the connect
      });
  }

  /**
   * Drops the pending resolver for `key` without resolving it - the caller
   * bailed (timeout or stop event); a late publication becomes a no-op.
   */
  cancelJob(key: string): void {
    if (this.#resolvers.has(key)) {
      this.#onResolverRemoved(key);
    }
  }

  async awaitJob(key: string, immediateCheck: () => Promise<JobReady | null>): Promise<JobReady> {
    this.#cancelIdleTimer();
    // Register the waiter BEFORE awaiting the connect so a caller bail during
    // a slow connect (the wrapper's 5s budget firing cancelJob) has an entry
    // to remove; otherwise the cancel would no-op and this method would
    // resume later, subscribing for a job nobody is waiting on.
    //
    // Concurrent observers of the same key share ONE entry, ONE subscription,
    // and ONE probe; every result path funnels through #resolveKey so all
    // waiters settle together. Only the first observer performs the connect,
    // subscribe, and probe setup.
    const existing = this.#resolvers.get(key);
    const entry: ResolverEntry = existing ?? {resolvers: [], rejecters: [], probeTimer: null, subscription: null};
    const isFirstObserver = existing === undefined;
    if (isFirstObserver) {
      this.#resolvers.set(key, entry);
    }
    const fromEvent = new Promise<JobReady>((resolve, reject) => {
      entry.resolvers.push(resolve);
      entry.rejecters.push(reject);
    });

    if (isFirstObserver) {
      try {
        await this.#ensureConnected();
      } catch (e) {
        this.#rejectKey(key, e);
        // Rejected via the fan-out when the entry still existed; if the caller
        // bailed first, the entry is gone and fromEvent stays pending forever,
        // which is exactly right - nobody is racing it anymore, and settling
        // it could only surface as an unhandled rejection.
        return fromEvent;
      }
      if (!this.#resolvers.has(key)) {
        // Cancelled during the connect (pending forever), or torn down by a
        // terminal disconnect (fromEvent already rejected with its error).
        return fromEvent;
      }
      if (this.#ns === null || this.#centrifuge === null) {
        this.#rejectKey(key, new Error('centrifugo-stream-client: namespace is empty after connect'));
        return fromEvent;
      }
      try {
        this.#subscribe(key);
      } catch (e) {
        // A channel-unsafe key (validated in centrifugoChannelForJob) or any
        // subscribe-time throw: fail every waiter loudly, matching PHP.
        this.#rejectKey(key, e);
        return fromEvent;
      }
      this.#startProbe(key, immediateCheck);
    }

    // The one-shot immediate check. A result fans out to every waiter via
    // #resolveKey; a rejection means the JOB errored, not the transport, so it
    // fans out as an ImmediateCheckError (callers fall back without logging a
    // transport failure) - and the fan-out is also the cleanup, releasing the
    // subscription and probe that would otherwise leak on every errored job.
    const fromCheckError = immediateCheck()
      .then((r): Promise<JobReady> => {
        if (r !== null) {
          this.#resolveKey(key, r);
        }
        return PENDING_FOREVER;
      })
      .catch((e: unknown): Promise<JobReady> => {
        this.#rejectKey(key, new ImmediateCheckError(e));
        return PENDING_FOREVER;
      });

    return Promise.race([fromEvent, fromCheckError]);
  }

  /**
   * Shared 30s store re-read for a key, same contract as the SSE client.
   * Removable once the centrifugo transport's probe-win telemetry stays at
   * zero through the soak (tracked in the migration plan).
   */
  #startProbe(key: string, immediateCheck: () => Promise<JobReady | null>): void {
    const entry = this.#resolvers.get(key);
    if (entry === undefined) {
      return;
    }
    entry.probeTimer = setInterval(() => {
      void immediateCheck()
        .then((r) => {
          if (r !== null) {
            this.#resolveKey(key, r);
          }
        })
        .catch(() => {
          // swallow: a transient probe failure must not abort the wait
        });
    }, PROBE_INTERVAL_MS);
  }

  /** Settle every waiter of `key` with a result and tear the entry down. */
  #resolveKey(key: string, ready: JobReady): void {
    const entry = this.#resolvers.get(key);
    if (entry === undefined) {
      return;
    }
    for (const resolve of entry.resolvers) {
      resolve(ready);
    }
    this.#onResolverRemoved(key);
  }

  /** Settle every waiter of `key` with a failure and tear the entry down. */
  #rejectKey(key: string, error: unknown): void {
    const entry = this.#resolvers.get(key);
    if (entry === undefined) {
      return;
    }
    for (const reject of entry.rejecters) {
      reject(error);
    }
    this.#onResolverRemoved(key);
  }

  /**
   * Subscribes the job's user-limited channel. `since: {}` opts into cache
   * recovery: if the publication landed before this subscribe (job finished
   * fast), Centrifugo delivers the latest publication immediately on subscribe
   * instead of the client waiting for a probe. A server rejection (permission,
   * bad channel) rejects the job's resolver as a SubscribeRejectedError so the
   * wrapper logs it under reason=subscribe, mirroring the SSE client.
   */
  #subscribe(key: string): void {
    if (this.#centrifuge === null || this.#ns === null) {
      return;
    }
    const entry = this.#resolvers.get(key);
    if (entry === undefined) {
      return;
    }
    const subscription = this.#centrifuge.newSubscription(centrifugoChannelForJob(this.#ns, key), {since: {}});
    entry.subscription = subscription;
    subscription.on('publication', (ctx) => {
      // Shape-guarded: a malformed publication must not throw inside the
      // centrifuge-js callback; it is simply not our job-ready event.
      const data: unknown = ctx.data;
      if (typeof data === 'object' && data !== null && (data as JobReady).key === key) {
        this.#resolveKey(key, data as JobReady);
      }
    });
    subscription.on('unsubscribed', (ctx) => {
      if (ctx.code >= SERVER_UNSUBSCRIBE_CODE_MIN) {
        this.#rejectKey(key, new SubscribeRejectedError(ctx.code));
      }
    });
    subscription.subscribe();
  }

  #onResolverRemoved(key: string): void {
    const entry = this.#resolvers.get(key);
    if (entry !== undefined) {
      if (entry.probeTimer !== null) {
        clearInterval(entry.probeTimer);
      }
      if (entry.subscription !== null && this.#centrifuge !== null) {
        entry.subscription.unsubscribe();
        this.#centrifuge.removeSubscription(entry.subscription);
      }
    }
    this.#resolvers.delete(key);
    this.#armIdleCloseIfIdle();
  }

  #armIdleCloseIfIdle(): void {
    if (this.#resolvers.size === 0) {
      this.#cancelIdleTimer();
      this.#idleTimer = setTimeout(() => {
        this.#idleTimer = null;
        if (this.#resolvers.size === 0) {
          this.#closeConnection();
        }
      }, IDLE_CLOSE_DELAY_MS);
    }
  }

  #cancelIdleTimer(): void {
    if (this.#idleTimer !== null) {
      clearTimeout(this.#idleTimer);
      this.#idleTimer = null;
    }
  }

  #closeConnection(): void {
    // Future: this and #armIdleCloseIfIdle are the SINGLE chokepoint for tearing
    // the socket down, and both assume this client solely owns the connection -
    // "idle" means "no jobs" (#resolvers.size === 0). The day the WebSocket
    // serves more than jobs, do NOT add other features' subscriptions onto this
    // class's #centrifuge (they would die whenever jobs go idle). Instead extract
    // a connection manager that owns the Centrifuge instance, refcounts every
    // consumer (jobs + the new feature), idle-closes only when the total refcount
    // hits zero, and broadcasts a disconnect event so each consumer fails its own
    // in-flight waiters. Keep teardown funnelled through one place to keep that swap local.
    //
    // In-flight waiters must fail promptly (their wrappers fall back to
    // polling now) rather than sit out the 30s probe on a dead transport.
    // Rejecting first, while #centrifuge is still set, lets the entry
    // teardown release each subscription.
    for (const key of Array.from(this.#resolvers.keys())) {
      this.#rejectKey(key, new Error('centrifugo-stream-client: terminal disconnect'));
    }
    // Null the instance field BEFORE disconnecting: disconnect() emits
    // 'disconnected', whose handler re-enters here unless its identity guard
    // sees the field already cleared.
    const centrifuge = this.#centrifuge;
    this.#centrifuge = null;
    this.#connected = false;
    this.#connecting = null;
    this.#ns = null;
    centrifuge?.disconnect();
    this.#cancelIdleTimer();
  }

  /**
   * Connects once and caches the namespace. The connection token is refreshed
   * by centrifuge-js via getToken (fixing the SSE client's known
   * never-refreshed-token gap); the namespace is re-captured on each mint so it
   * tracks the session's auth state.
   */
  #ensureConnected(): Promise<void> {
    if (this.#connected) {
      return Promise.resolve();
    }
    if (this.#connecting !== null) {
      return this.#connecting;
    }
    const connecting = (async (): Promise<void> => {
      const {token, ns} = await this.deps.getToken();
      this.#ns = ns;
      const websocketUrl = `${this.#clientBaseUrl.replace(/^http/, 'ws')}/connection/websocket`;
      const getToken = async (): Promise<string> => {
        const refreshed = await this.deps.getToken();
        this.#ns = refreshed.ns;
        return refreshed.token;
      };
      const centrifuge =
        this.deps.centrifugeFactory?.(websocketUrl, {getToken}) ?? new Centrifuge(websocketUrl, {token, getToken});
      this.#centrifuge = centrifuge;
      centrifuge.on('connected', () => {
        this.#connected = true;
      });
      centrifuge.on('disconnected', () => {
        this.#connected = false;
        // 'disconnected' is TERMINAL in centrifuge-js (transient transport
        // failures emit 'connecting' and retry internally). A post-connect
        // terminal state (token-refresh failure, server force-disconnect)
        // would otherwise leave the resolved #connecting cached, and every
        // later awaitJob would subscribe against this dead client. Tear down
        // so the next call rebuilds from scratch. The identity guard keeps
        // our own #closeConnection() disconnect from re-entering.
        if (this.#centrifuge === centrifuge) {
          this.#closeConnection();
        }
      });
      await new Promise<void>((resolve, reject) => {
        // Terminal failures (invalid/expired token, unauthorized) emit
        // 'disconnected' WITHOUT retrying; transient transport failures emit
        // 'connecting' and centrifuge-js retries them itself. Rejecting here
        // (and only here) keeps a dead token from leaving #connecting pending
        // forever, which would silently pin every later awaitJob/warm on a
        // promise that can never settle.
        const onConnected = (): void => {
          centrifuge.off('disconnected', onDisconnected);
          resolve();
        };
        const onDisconnected = (ctx: {code: number; reason: string}): void => {
          centrifuge.off('connected', onConnected);
          reject(new Error(`centrifugo-stream-client: terminal disconnect during connect: ${ctx.code} ${ctx.reason}`));
        };
        centrifuge.once('connected', onConnected);
        centrifuge.once('disconnected', onDisconnected);
        centrifuge.connect();
      });
    })();
    // If the initial connect fails (token fetch throws), tear down so a later
    // awaitJob retries from scratch. centrifuge-js itself retries transport
    // failures with backoff, so this only fires on the token path.
    connecting.catch(() => {
      if (this.#connecting === connecting) {
        this.#closeConnection();
      }
    });
    this.#connecting = connecting;
    return connecting;
  }
}
